import { DatePipe } from '@angular/common'
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core'
import { AbstractControl, FormGroup } from '@angular/forms'
import { ComponentState, DateTimeConfig, InputType, ListConfig, RadioConfig } from '@app/models/shared.model'
import { DateService } from '@app/shared/services/date.service'
import { InputMaskService } from '@app/shared/services/input-mask.service'
import { TranslateService } from '@ngx-translate/core'
import { isBoolean, isDate } from 'lodash-es'
import { MessageService } from 'primeng/api'

/** Option row held in listConfig.data — template keys are dynamic, id/id_number are the known fields */
type ListOption = Record<string, unknown> & { id?: number | string; id_number?: string }

@Component({
  selector: 'app-yabi-input',
  templateUrl: './yabi-input.component.html',
  styleUrls: ['./yabi-input.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class YabiInputComponent implements OnInit, OnChanges {
  /** Input type */
  @Input() type: InputType = 'text'

  /** Input id */
  @Input() id: string = ''

  /** Range second ID */
  @Input() rangSecondId: string = ''

  /** Input labels shoeing when input is editable */
  @Input() label?: string

  /** Disabled input? */
  @Input() disabled?: boolean

  /** Editable input? */
  @Input() editable?: boolean = true

  /** Readonly input? */
  @Input() readonly?: boolean

  /** Input placeholder */
  @Input() placeholder?: string

  /** Input container class */
  @Input() class?: string = 'p-input-filled'

  /** Input container style */
  @Input() style?: string

  /** Form group */
  @Input() form?: FormGroup

  @Input() formControlNameInput?: string

  @Input() rangeFormControlNameInput?: string

  /** Init placeholder by label input */
  @Input() placeholderByLabel: boolean = false

  /** Text area rows number use inly when the input type is textarea */
  @Input() textAreaRows: number = 2

  /** Dropdown/multi-select list config use when the input type in dropdown/multiselect */
  @Input() listConfig: ListConfig = new ListConfig({ data: [] })

  /** Date time config use when the input type is date/time/timeRange/DateRange */
  @Input() dateTimeConfig: DateTimeConfig

  /** Bind value by model */
  @Input() ngModelValue: any

  /** Bind value by model */
  @Output() ngModelValueChange = new EventEmitter<any>()

  /** Bind second value by model (when has range) */
  @Input() ngModelSecondValue: any

  /** Function for use for get data like labels etc... */
  @Input() labelCallbackFunction: (args: any) => any

  /** Condition callback function */
  @Input() conditionCallbackFunction: (args: any) => any

  /** Radio config use when the type is radio  */
  @Input() radioConfig: RadioConfig

  /** Input wrapper class */
  @Input() wrapperClass: string

  /** Input label class */
  @Input() labelClass: string

  /** NgModel invalid input class */
  @Input() ngModelInvalidInput: boolean

  /** Bind second value by model (when has range) */
  @Output() ngModelSecondValueChange = new EventEmitter<any>()

  /** Row use when the input is from the table (use for check dateTime) */
  @Input() row: any

  /** State of component details/edit/create */
  @Input() state: ComponentState

  /** Input tab index */
  @Input() tabindex: number | null = null

  /** Country code key by default is 'country_code'*/
  @Input() countryCodeKey: string = 'country_code'

  /** Mobile key by default is 'mobile' */
  @Input() mobileKey: string = 'mobile'

  /** Is mobile input required? */
  @Input() mobileIsRequired: boolean = true

  /** Set default country code by client (for mobile inputs) */
  @Input() setDefaultCodeByClient: boolean

  /** Should the input number is decimal */
  @Input() numberInputDecimal: number

  @Input() searchNumberInput: {
    min?: number
    max?: number
    useGrouping?: boolean
  } | null = null

  @Input() textSearch: {
    useGrouping?: boolean
  } | null = null

  onMultiselectPanelShow(): void {
    requestAnimationFrame(() => {
      const panels = document.querySelectorAll<HTMLElement>('.p-multiselect-overlay')
      const panel = panels[panels.length - 1]
      if (!panel) return
      const rect = panel.getBoundingClientRect()
      if (rect.bottom > window.innerHeight) {
        panel.style.top = `${parseFloat(panel.style.top) - (rect.bottom - window.innerHeight) - 8}px`
      }
    })
  }

  /** On click on input  */
  @Output() onClick = new EventEmitter<any>()

  /** On change output */
  @Output() onChange = new EventEmitter<any>()

  /** On lazy loading using in lists  */
  @Output() onLazyLoad = new EventEmitter<any>()

  constructor(
    public maskService: InputMaskService,
    public dateService: DateService,
    private datePipe: DatePipe,
    private translate: TranslateService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.placeholderByLabel) this.placeholder = this.label
    else if (!this.placeholder) this.placeholder = ''

    // Handle initial disabled state
    this.updateDisabledState()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.updateDisabledState()
    }
    // Handle any other input changes that might affect the view
    // Defer to next tick to avoid ExpressionChangedAfterItHasBeenCheckedError
    if (changes['listConfig'] || changes['options'] || changes['ngModelValue']) {
      setTimeout(() => this.cdr.markForCheck())
    }
  }

  private updateDisabledState(): void {
    // Handle formControlNameInput with form
    if (this.form && this.formControlNameInput) {
      const control = this.form.get(this.formControlNameInput)
      if (control) {
        if (this.disabled) {
          control.disable({ emitEvent: false })
        } else {
          control.enable({ emitEvent: false })
        }
      }
    }

    // Handle rangeFormControlNameInput with form
    if (this.form && this.rangeFormControlNameInput) {
      const rangeControl = this.form.get(this.rangeFormControlNameInput)
      if (rangeControl) {
        if (this.disabled) {
          rangeControl.disable({ emitEvent: false })
        } else {
          rangeControl.enable({ emitEvent: false })
        }
      }
    }

    // Handle dynamic dateTimeConfig form controls
    if (this.form && this.dateTimeConfig) {
      if (this.dateTimeConfig.firstRangeKey!) {
        const firstRangeControl = this.form.get(this.dateTimeConfig.firstRangeKey!)
        if (firstRangeControl) {
          if (this.disabled) {
            firstRangeControl.disable({ emitEvent: false })
          } else {
            firstRangeControl.enable({ emitEvent: false })
          }
        }
      }

      if (this.dateTimeConfig.secondRangeKey!) {
        const secondRangeControl = this.form.get(this.dateTimeConfig.secondRangeKey!)
        if (secondRangeControl) {
          if (this.disabled) {
            secondRangeControl.disable({ emitEvent: false })
          } else {
            secondRangeControl.enable({ emitEvent: false })
          }
        }
      }
    }

    // Handle mobile components that use country_code and mobile form controls
    if (this.form && this.type === 'mobile') {
      const countryControl = this.form.get(this.countryCodeKey)
      const mobileControl = this.form.get(this.mobileKey)

      if (countryControl) {
        if (this.disabled) {
          countryControl.disable({ emitEvent: false })
        } else {
          countryControl.enable({ emitEvent: false })
        }
      }

      if (mobileControl) {
        if (this.disabled) {
          mobileControl.disable({ emitEvent: false })
        } else {
          mobileControl.enable({ emitEvent: false })
        }
      }
    }
  }

  /**
   * Is field is required
   * @param key Form key
   * @returns True/false
   */
  isRequired(key: string): boolean {
    const control: AbstractControl | null = this.form?.get(key) ?? null
    if (!control) {
      return false
    }
    return !!(control.validator && !control.valid && !control.disabled)
  }

  /**
   * Get list config selected object by key
   * @param value Value
   * @returns list config selected object
   */
  getListConfigObjectByKey(value: string) {
    return (this.listConfig.data as ListOption[]).find((v) => (v[this.listConfig.optionValue!] as string | number | undefined)?.toString() == value?.toString())
  }

  /**
   * Check hours range is valid
   * @param isEnterDate
   */
  timeRangeIsIsValid(isEnterDate: boolean) {
    if (this.dateTimeConfig?.beforeDataForCheckSameDays) {
      const before = (this.dateTimeConfig?.beforeDataForCheckSameDays as Record<string, string | number | Date>[]).find(
        (v: any) =>
          v[this.dateTimeConfig.dayKey!] == this.row[this.dateTimeConfig.dayKey!] &&
          v[this.dateTimeConfig.additionalConditionKey!].toString() == this.row[this.dateTimeConfig.dayKey!].toString()
      )

      if (
        (!this.row?.[this.dateTimeConfig.firstRangeKey!] || !this.row?.[this.dateTimeConfig.secondRangeKey!]?.[this.dateTimeConfig.secondRangeKey!]) &&
        (before?.[this.dateTimeConfig.firstRangeKey!] || before?.[this.dateTimeConfig.secondRangeKey!])
      ) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('REPORTS.DELETE_ROW_TIME_TITLE'),
          detail: this.translate.instant('REPORTS.DELETE_ROW_TIME_MESSAGE'),
        })
        if (isEnterDate && isDate(this.row[this.dateTimeConfig.firstRangeKey!]))
          this.row[this.dateTimeConfig.firstRangeKey!] = this.datePipe.transform(new Date(before?.[this.dateTimeConfig.firstRangeKey!].toString()), 'HH:mm')
        else
          this.row[this.dateTimeConfig.secondRangeKey!] = this.row[this.dateTimeConfig.secondRangeKey!] = this.datePipe.transform(
            new Date(before?.[this.dateTimeConfig.secondRangeKey!].toString()),
            'HH:mm'
          )
      }
      if (!this.maskService.validateTime(this.row?.[this.dateTimeConfig.firstRangeKey!])) this.row[this.dateTimeConfig.firstRangeKey!] = ''
      if (!this.maskService.validateTime(this.row?.[this.dateTimeConfig.secondRangeKey!])) this.row[this.dateTimeConfig.secondRangeKey!] = ''

      if (!this.maskService.maskTimeValidateFirsTimeGreaterThanSecond(this.row?.[this.dateTimeConfig.firstRangeKey!], this.row?.[this.dateTimeConfig.secondRangeKey!])) {
        if (isEnterDate) this.row[this.dateTimeConfig.firstRangeKey!] = ''
        else this.row[this.dateTimeConfig.secondRangeKey!] = ''
      }

      const sameDays = (this.dateTimeConfig.currentDataForCheckSameDays as Record<string, unknown[]>)[this.dateTimeConfig.hoursKey!].filter(
        (v: any) => (v[this.dateTimeConfig.dayKey!] === this.row.id || v[this.dateTimeConfig.dayKey!] == this.row[this.dateTimeConfig.dayKey!]) && v != this.row
      )

      if (sameDays?.length) {
        sameDays.forEach((element: any) => {
          const e1Start = this.row[this.dateTimeConfig.firstRangeKey!]
            ? this.dateService.convertTimeFromStringNormal(
                this.row?.[this.dateTimeConfig.firstRangeKey!],
                this.row.id ? new Date(this.row.id) : new Date(element[this.dateTimeConfig.dayKey!])
              )
            : null
          const e1End = this.row[this.dateTimeConfig.secondRangeKey!]
            ? this.dateService.convertTimeFromStringNormal(
                this.row?.[this.dateTimeConfig.secondRangeKey!],
                this.row.id ? new Date(this.row.id) : new Date(element[this.dateTimeConfig.dayKey!])
              )
            : null
          const e2Start = element[this.dateTimeConfig.firstRangeKey!]
            ? this.dateService.convertTimeFromStringNormal(element?.[this.dateTimeConfig.firstRangeKey!], new Date(element[this.dateTimeConfig.dayKey!]))
            : null
          const e2End = element?.[this.dateTimeConfig.secondRangeKey!]
            ? this.dateService.convertTimeFromStringNormal(element?.[this.dateTimeConfig.secondRangeKey!], new Date(element[this.dateTimeConfig.dayKey!]))
            : null

          if (this.dateService.dateRangeOverlaps(e2Start!, e2End!, e1Start!, e1End!)) {
            if (isEnterDate) this.row[this.dateTimeConfig.firstRangeKey!] = ''
            else this.row[this.dateTimeConfig.secondRangeKey!] = ''
            return
          }
        })
      }
    } else {
      if (!this.maskService.maskTimeValidateFirsTimeGreaterThanSecond(this.ngModelValue, this.ngModelSecondValue)) {
        if (isEnterDate) this.onChange.emit(null)
        else this.onChange.emit(null)
      } else {
        if (isEnterDate) this.onChange.emit(this.ngModelValue)
        else this.onChange.emit(this.ngModelSecondValue)
      }
    }
  }

  getListMultiSelectedItemsTemplate(val: any) {
    if (!this.listConfig?.optionValue) return `${val?.[this.listConfig.templateKey!]} ${val?.[this.listConfig.secondTemplateKey!] || ''}`

    if (this.listConfig.data?.length) {
      const foundItem = (this.listConfig.data as ListOption[])?.find((v) => v.id?.toString() == val?.toString())
      if (foundItem) {
        return `${foundItem[this.listConfig.templateKey!] || ''} ${foundItem[this.listConfig.secondTemplateKey!] || ''}`
      }
    }
    return null
  }

  getListMultiSelectedItemsTemplateWithId(val: any) {
    if (!this.listConfig?.optionValue) {
      const baseText = `${val?.[this.listConfig.templateKey!]} ${val?.[this.listConfig.secondTemplateKey!] || ''}`
      return val?.id_number ? `${baseText} (${val.id_number})` : baseText
    }

    if (this.listConfig.data?.length) {
      const foundItem = (this.listConfig.data as ListOption[])?.find((v) => v.id?.toString() == val?.toString())
      if (foundItem) {
        const baseText = `${foundItem[this.listConfig.templateKey!] || ''} ${foundItem[this.listConfig.secondTemplateKey!] || ''}`
        return foundItem.id_number ? `${baseText} (${foundItem.id_number})` : baseText
      }
    }
    return null
  }

  /**
   * Boolean get translate
   * @param value Value if no value return empty else return allow or deny
   * @returns
   */
  booleanTranslate(value: any) {
    const booleanValue = !isBoolean(value) ? (value ? true : false) : value
    return `COMMON.TAGS.${booleanValue ? 'ALLOW' : 'DENY'}`
  }
}
